import React, { useEffect, useRef, useState } from 'react';
import { ToolCardProposal } from '../../../types/chat';
import { CONFIRM_MESSAGE } from '../../../utils/toolCalls';

interface ProposalCardBodyProps {
  proposal: ToolCardProposal;
  executed: boolean;
  expired: boolean;
  disabled: boolean;
  onToolAction?: (text: string) => void;
}

/**
 * 提案カードの本体: 概算クレジットと「この内容で生成する／やめる」。
 * 確認はユーザーメッセージとして送られ、サーバーがコードを検証する。
 */
const ProposalCardBody: React.FC<ProposalCardBodyProps> = ({ proposal, executed, expired, disabled, onToolAction }) => {
  const [sent, setSent] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // 確認を送ったのに実行されないまま応答が終わった（disabled が解けた）ら、もう一度押せるように戻す。
  // 戻さないと「送信中…」のまま固まり、ユーザーが再確認できない。
  const wasDisabled = useRef(disabled);
  useEffect(() => {
    if (wasDisabled.current && !disabled && !executed) {
      setSent(false);
    }
    wasDisabled.current = disabled;
  }, [disabled, executed]);

  if (executed) {
    return <p className="text-xs text-gray-500">この提案は実行済みです。</p>;
  }
  if (expired) {
    // 状態ピルが「期限切れ」を出すため、本文では同じ語を重ねない（読み上げ・検索の重複を避ける）。
    return <p className="text-xs text-gray-500">この提案は有効期限が切れています。もう一度依頼してください。</p>;
  }
  if (dismissed) {
    return <p className="text-xs text-gray-500">生成をやめました。</p>;
  }

  const canAct = Boolean(onToolAction) && !disabled && !sent;

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-amber-800">
          概算 <span className="text-sm font-semibold tabular-nums">{proposal.estimated_credits.toLocaleString()}</span> クレジットを消費します
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="rounded-md px-2.5 py-1 text-xs text-gray-600 hover:bg-gray-100 disabled:opacity-50"
            disabled={!canAct}
            onClick={() => setDismissed(true)}
          >
            やめる
          </button>
          <button
            type="button"
            className="rounded-md bg-violet-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={!canAct}
            onClick={() => {
              setSent(true);
              onToolAction?.(CONFIRM_MESSAGE(proposal.code));
            }}
          >
            {sent ? '送信中…' : 'この内容で生成する'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProposalCardBody;
